import React, { useEffect, useState } from "react";
import { Mail, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, endpoints } from "@/lib/api";
import { toast } from "sonner";

const ISOQUEST = ["admin", "staff", "consultant"];
const fmt = (d) => (d ? new Date(d).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

const SentLog = ({ log }) => (
  <div data-testid="share-sent-log" className="space-y-1.5 max-h-40 overflow-y-auto">
    {log.length === 0 && <p className="text-xs text-slate-500">Not sent yet.</p>}
    {log.map((l) => (
      <div key={l.id} className="text-xs rounded-md border border-slate-200 px-2.5 py-1.5">
        <div className="font-medium">{fmt(l.sent_at)} · by {l.sent_by}</div>
        <div className="text-slate-500">
          {l.recipients.map((r) => `${r.email}${r.email_sent ? "" : " (failed)"}`).join(", ")}
          {l.download_count ? ` · opened ${l.download_count}×` : ""}
        </div>
        {l.note && <div className="text-slate-600 italic mt-0.5 line-clamp-2">"{l.note}"</div>}
      </div>
    ))}
  </div>
);

export const SendToClientButton = ({ kind, refId, year, clientSlug, size = "sm" }) => {
  const [allowed, setAllowed] = useState(false);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const params = { kind, ref_id: refId || undefined, year: year || undefined, client_slug: clientSlug || undefined };

  useEffect(() => { endpoints.me().then((u) => setAllowed(ISOQUEST.includes(u.canonical_role))).catch(() => {}); }, []);

  const load = () => api.get("/shared-pdfs/recipients", { params }).then((r) => setData(r.data))
    .catch((e) => { toast.error(e.response?.data?.detail || "Could not load contacts"); setOpen(false); });

  useEffect(() => {
    if (open) { setData(null); setPicked([]); setNote(""); load(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggle = (email) => setPicked((p) => (p.includes(email) ? p.filter((x) => x !== email) : [...p, email]));

  const send = async () => {
    setBusy(true);
    try {
      await api.post("/shared-pdfs/send", { ...params, contact_emails: picked, note });
      toast.success(`Sent to ${picked.length} contact${picked.length > 1 ? "s" : ""}`);
      setPicked([]); setNote(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); load(); } finally { setBusy(false); }
  };

  if (!allowed) return null;
  return (
    <>
      <Button type="button" variant="outline" size={size} onClick={() => setOpen(true)} data-testid={`share-${kind}-button`}>
        <Mail className="h-3.5 w-3.5 mr-1" /> Send to client
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="share-dialog" className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Send PDF to client</DialogTitle>
            <DialogDescription>{data?.client_name ? `Email a download link to contacts at ${data.client_name}.` : "Loading contacts..."}</DialogDescription>
          </DialogHeader>
          {data && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Contacts</Label>
                {data.contacts.length === 0 && (
                  <p data-testid="share-no-contacts" className="text-xs text-amber-600">This client has no saved contacts with an email address. Add one on the client's profile first.</p>
                )}
                {data.contacts.map((c) => (
                  <label key={c.email} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={picked.includes(c.email)} onCheckedChange={() => toggle(c.email)} data-testid={`share-contact-${c.email}`} />
                    <span className="font-medium">{c.name || c.email}</span>
                    <span className="text-xs text-slate-500 truncate">{c.role ? `${c.role} · ` : ""}{c.email}</span>
                  </label>
                ))}
              </div>
              <div>
                <Label htmlFor="share-note">Personal note (optional)</Label>
                <Textarea id="share-note" data-testid="share-note-input" rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a short message for the client" />
              </div>
              <div className="space-y-1.5">
                <Label>Sent before</Label>
                <SentLog log={data.log} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Close</Button>
            <Button type="button" data-testid="share-send-button" className="brand-gradient text-white" disabled={busy || !picked.length} onClick={send}>
              <Send className="h-4 w-4 mr-1" /> {busy ? "Sending..." : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
